import { useEffect, useState, useRef } from 'react';
import { getBootstrapIcons } from '../../utils/utils';
import ColorBar from '../theme/ColorBar';
import { FileDrop } from '../helpers/FileDrop';
import { CharacterLimitIndicator } from '../helpers/CharacterLimitIndicator';
import RichTextEditor from './RichTextEditor';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import SSODropdown from '../shared/asyncDropdowns/SSODropdown';
import { sanitizeFileName } from '../helpers/FileUtils';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import { uploadFile } from '../../services/containerService';
import Toast from '../ui/toasts/Toast';
import {
    formatFileSize,
    getAudioLength,
    getFileCategory,
    getImageDimensions,
    getVideoRuntime,
} from '../helpers/FileUtils';
import { createAsset, fetchAssets } from '../../services/clientService';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { Dropdown } from '../helpers/Dropdown';
import { queryPortalPages } from '../../services/portalPageService';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';

const PropertiesLink = ({
    item,
    setItem,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
    portalId,
}) => {
    const { publicBrand } = useAuthContext();
    const { enqueueUpload } = useUploadContext();
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [imageAsset, setImageAsset] = useState({});
    const [textAssets, setTextAssets] = useState([]);
    const [containerAssets, setContainerAssets] = useState([]);
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);

    const [selectedUrl, setSelectedUrl] = useState('');

    const [selectedSSO, setSelectedSSO] = useState(null);
    const [isDisplayIcon, setIsDisplayIcon] = useState(false);
    const [file, setFile] = useState();
    const [selectedIcon, setSelectedIcon] = useState('');
    const [selectedBackgroundColor, setSelectedBackgroundColor] = useState(
        publicBrand.accessoryColor,
    );
    const [selectedForegroundColor, setSelectedForegroundColor] = useState(
        publicBrand.accessoryTextColor,
    );
    const [selectedButtonText, setSelectedButtonText] = useState('');
    const [selectedCaption, setSelectedCaption] = useState('');
    const [image, setImage] = useState(null);
    const [initialText, setInitialText] = useState('');
    const [text, setText] = useState('');
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [activeCard, setActiveCard] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [typeSelection, setTypeSelection] = useState(null);
    const [selectedPortal, setSelectedPortal] = useState({});
    const [eligiblePortalsList, setEligiblePortalsList] = useState([]);
    const [selectedMenuRoute, setSelectedMenuRoute] = useState('');

    const urlInputRef = useRef(null);

    const isPopulatingRef = useRef(false);
    const isEditingRef = useRef(false);

    const handleFileChange = fileValue => {
        setFile(sanitizeFileName(fileValue));
    };

    const handleImageCaptionChange = event => {
        setSelectedCaption(event.target.value);
    };

    const handleButtonLabelChange = event => {
        setSelectedButtonText(event.target.value);
    };

    const handleAssetDelete = assetId => {
        setContainerAssets(prevAssets =>
            prevAssets.filter(asset => asset.assetId !== assetId),
        );
    };

    const findPortalPageName = childPortalId => {
        const portalPage = eligiblePortalsList.find(
            page => page.value === childPortalId,
        );
        return portalPage ? portalPage.label : `Portal ID: ${childPortalId}`;
    };

    const typeOptions = [
        { label: getText('msg_2e8a57cc5c47'), value: 1 },
        { label: getText('msg_b55223c7bdc7'), value: 2 },
        { label: getText('msg_5fa55a6001cc'), value: 3 },
        { label: getText('msg_57f5f5efbc59'), value: 4 },
    ];

    const menuRouteOptions = [
        { label: getText('msg_927f40673764'), value: ROUTES.MY_BENEFITS },
        { label: getText('msg_f1702b468627'), value: ROUTES.MY_MESSAGES },
        { label: getText('msg_20e338624cee'), value: ROUTES.MY_ASSETS },
    ];

    const getLabelForMenuRoute = routeValue => {
        const option = menuRouteOptions.find(opt => opt.value === routeValue);
        return option ? option.label : 'Select';
    };

    const getLabelForValue = value => {
        const option = typeOptions.find(opt => opt.value === value);
        return option ? option.label : 'Select Type';
    };

    useEffect(() => {
        let portalsArray = [];
        queryPortalPages(null)
            .then(payload => {
                payload.viewModels.forEach(portalPage => {
                    if (portalPage.status == 1 && portalPage.id != portalId) {
                        portalsArray.push({
                            value: portalPage.id,
                            label:
                                portalPage.name +
                                (portalPage.isChild == false ? ' (M)' : ''),
                        });
                    }
                    if (
                        item &&
                        item.hasOwnProperty('linkPart') &&
                        item.linkPart !== null
                    ) {
                        if (item.linkPart.childPortalId === portalPage.id) {
                            setSelectedPortal({
                                value: portalPage.id,
                                label: portalPage.name,
                            });
                        }
                    }
                });
                setEligiblePortalsList(portalsArray);
            })
            .catch(err => {
                console.error('Failed to query portal pages', err);
            });
    }, [portalId]);

    const menuRouteValues = [
        ROUTES.MY_BENEFITS,
        ROUTES.MY_MESSAGES,
        ROUTES.MY_ASSETS,
    ];

    useEffect(() => {
        if (!typeSelection && item && eligiblePortalsList) {
            if (item?.linkPart) {
                if (
                    item.linkPart.url &&
                    menuRouteValues.includes(item.linkPart.url)
                ) {
                    setSelectedMenuRoute(item.linkPart.url);
                    setTypeSelection(4);
                } else if (item.linkPart.url) {
                    setTypeSelection(1);
                } else if (item.linkPart.tenantSSOId) {
                    setSelectedSSO(item.linkPart.tenantSSOId);
                    setTypeSelection(3);
                } else if (item.linkPart.childPortalId) {
                    const label = findPortalPageName(
                        item.linkPart.childPortalId,
                    );
                    setTypeSelection(2);
                    setSelectedPortal({
                        value: item.linkPart.childPortalId,
                        label: label,
                    });
                }
            }
        }
    }, [item, eligiblePortalsList]);

    useEffect(() => {
        if (
            item !== undefined &&
            item !== null &&
            item.hasOwnProperty('linkPart') &&
            item.linkPart !== null
        ) {
            isPopulatingRef.current = true;

            try {
                if (item.hasOwnProperty('templateStatus')) {
                    if (item.templateStatus === -1) {
                        setAreInputsDisabled(true);
                    } else {
                        setAreInputsDisabled(false);
                    }
                }

                const lp = item.linkPart || {};

                // For Website: set the full URL into selectedUrl (no separate normalized value)
                if (lp.url && lp.url !== '') {
                    setSelectedUrl(lp.url);
                } else {
                    setSelectedUrl('');
                }

                if (lp.tenantSSOId) {
                    setSelectedSSO(lp.tenantSSOId);
                }

                if (lp.glyph !== null) {
                    setIsDisplayIcon(true);
                    setSelectedIcon(lp.glyph);
                    setSelectedBackgroundColor(lp.backgroundColor);
                    setSelectedForegroundColor(lp.foregroundColor);
                } else {
                    setIsDisplayIcon(false);
                    setSelectedButtonText(lp.buttonText);
                    setImage(lp.image);
                    setSelectedCaption(lp.caption);
                    setInitialText(lp.text);
                    setText(lp.text);
                }
            } finally {
                Promise.resolve().then(() => {
                    isPopulatingRef.current = false;
                });
            }
        } else {
            setIsDisplayIcon(true);
            setSelectedButtonText('View');
        }
    }, []);

    useEffect(() => {
        setContainerAssets([imageAsset, ...textAssets]);
    }, [imageAsset, textAssets]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets();
        }
    }, [currentPage, searchTerm]);

    function updateAsset(asset, key, value) {
        return { ...asset, [key]: value };
    }

    const uploadAsset = async () => {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                } else {
                    urlResponse = encodeURI(response);
                }
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch (error) {
                    Toast.error(getText('msg_3e298875de25'));
                }
            } catch (error) {
                Toast.error(getText('msg_3e298875de25'));
            }
        }

        if (isValidUrl || asset.url) {
            const newAsset = { ...asset };
            if (!newAsset.assetTypeId) newAsset.assetTypeId = 4; //ContentInProgress
            if (urlResponse) newAsset.url = urlResponse;
            newAsset.fileType = file.name.split('.').pop();
            newAsset.fileSize = formatFileSize(file.size);

            const fileCategory = getFileCategory(file.name);
            if (fileCategory === 'video') {
                newAsset.metaData = await getVideoRuntime(file);
            } else if (fileCategory === 'audio') {
                newAsset.metaData = await getAudioLength(file);
            } else if (fileCategory === 'image') {
                newAsset.metaData = await getImageDimensions(file);
            }

            const createResponse = await createAsset(newAsset, null);
            if (createResponse.error) {
                Toast.error(createResponse.message);
            } else if (
                createResponse.status === 200 &&
                createResponse.data.url
            ) {
                Toast.success(getText('msg_9309271e92fe'));
                switch (isUploadFromDeviceChecked) {
                    case 'image':
                        setImage(createResponse.data.url);
                        setImageAsset({
                            assetId: createResponse.data.id,
                            assetTypeId: createResponse.data.assetTypeId,
                            url: createResponse.data.url,
                        });
                        break;
                    case 'text':
                        setTextAssets(prevTextAssets => {
                            // Only add the asset if it doesn't already exist

                            const assetExists = prevTextAssets.some(
                                asset =>
                                    asset.assetId === createResponse.data.id,
                            );
                            if (!assetExists) {
                                return [
                                    ...prevTextAssets,
                                    {
                                        assetId: createResponse.data.id,
                                        assetTypeId:
                                            createResponse.data.assetTypeId,
                                        url: createResponse.data.url,
                                    },
                                ];
                            }
                            return prevTextAssets; // Return the previous state if the asset already exists
                        });
                        break;
                    default:
                        break;
                }
                setIsUploadFromDeviceChecked('');
                setAsset({});
                setFile(null);
            } else {
                Toast.error(getText('msg_9f8fd8f97fd3'));
            }
        }

        setIsSaving(false);
    };

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    };

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const handleClick = index => {
        setActiveCard(activeCard === index ? null : index);
    };

    const addAsset = () => {
        if (activeCard === null) {
            return Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
        }

        const assetAdding = assets[activeCard];
        switch (isAddFromAssetsChecked) {
            case 'image':
                setImage(assetAdding.url);
                setImageAsset({
                    assetId: assetAdding.id,
                    url: assetAdding.url,
                });
                break;
            case 'text':
                setTextAssets(prevTextAssets => {
                    // Only add the asset if it doesn't already exist

                    const assetExists = prevTextAssets.some(
                        asset => asset.assetId === assetAdding.id,
                    );
                    if (!assetExists) {
                        return [
                            ...prevTextAssets,
                            { assetId: assetAdding.id, url: assetAdding.url },
                        ];
                    }
                    return prevTextAssets; // Return the previous state if the asset already exists
                });
                break;
            default:
                break;
        }
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    };

    useEffect(() => {
        handleUploadFromDevice(isUploadFromDeviceChecked);
    }, [isUploadFromDeviceChecked]);

    const loadAssets = async () => {
        setIsSaving(true);
        try {
            let queryParams = {
                assetTypes: [1, 2],
                page: currentPage,
                pageSize: 12,
                fileTypes: ['jpg', 'jpeg', 'png', 'svg'],
            };
            if (searchTerm !== '' && searchTerm !== ' ') {
                queryParams = { ...queryParams, search: searchTerm };
            }
            const fetchedAssets = await fetchAssets(queryParams, null, false);
            setAssets(fetchedAssets.viewModels);
            setTotalPages(fetchedAssets.pageTotal);
        } catch (error) {
            console.error('Failed to fetch assets', error);
            Toast.error(getText('msg_cade149a85b2'));
        }
        setIsSaving(false);
    };

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets();
        }
        handleAddFromAssets(isAddFromAssetsChecked);
    }, [isAddFromAssetsChecked]);

    useEffect(() => {
        if (isDisplayIcon) {
            setImage(null);
            setImageAsset({});
        }
    }, [isDisplayIcon]);

    const handleSSOChange = tenantSSOId => {
        setSelectedSSO(tenantSSOId);
    };

    useEffect(() => {
        if (isPopulatingRef.current) return;

        setItem(prevState => {
            const { linkPart } = prevState || {};
            const currentLinkPart = linkPart || {};

            const newLinkPart = {
                ...(currentLinkPart || {}),
                url:
                    typeSelection === 1
                        ? selectedUrl || ''
                        : typeSelection === 4
                          ? selectedMenuRoute || ''
                          : '',
                tenantSSOId: typeSelection === 3 ? selectedSSO : null,
                childPortalId:
                    typeSelection === 2
                        ? (selectedPortal?.value ?? null)
                        : null,
                glyph: isDisplayIcon ? selectedIcon : null,
                backgroundColor: isDisplayIcon ? selectedBackgroundColor : null,
                foregroundColor: isDisplayIcon ? selectedForegroundColor : null,
                image: isDisplayIcon ? null : image,
                caption: isDisplayIcon ? null : selectedCaption,
                text: isDisplayIcon ? null : text,
                buttonText: isDisplayIcon ? null : selectedButtonText,
            };

            const noChange =
                (currentLinkPart?.url || '') === (newLinkPart.url || '') &&
                (currentLinkPart?.tenantSSOId || null) ===
                    (newLinkPart.tenantSSOId || null) &&
                (currentLinkPart?.childPortalId || null) ===
                    (newLinkPart.childPortalId || null) &&
                (currentLinkPart?.glyph || null) ===
                    (newLinkPart.glyph || null) &&
                (currentLinkPart?.backgroundColor || null) ===
                    (newLinkPart.backgroundColor || null) &&
                (currentLinkPart?.foregroundColor || null) ===
                    (newLinkPart.foregroundColor || null) &&
                (currentLinkPart?.image || null) ===
                    (newLinkPart.image || null) &&
                (currentLinkPart?.caption || null) ===
                    (newLinkPart.caption || null) &&
                (currentLinkPart?.text || null) ===
                    (newLinkPart.text || null) &&
                (currentLinkPart?.buttonText || null) ===
                    (newLinkPart.buttonText || null);

            if (noChange) return prevState;

            return {
                ...prevState,
                linkPart: {
                    ...newLinkPart,
                },
                containerAssets: containerAssets,
            };
        });
    }, [
        selectedUrl,
        selectedSSO,
        selectedPortal,
        selectedMenuRoute,
        typeSelection,
        selectedIcon,
        selectedBackgroundColor,
        selectedForegroundColor,
        selectedCaption,
        text,
        image,
        selectedButtonText,
        containerAssets,
        isDisplayIcon,
        setItem,
    ]);

    // Array list of all Bootstrap v5.3 Icons extracted from https://icons.getbootstrap.com/sprite/
    // TODO: Replace with Emy's custom icon library once that one is ready
    const icons = getBootstrapIcons();

    // Filter the icon array by the user's search input
    const [filter, setFilter] = useState('');
    const filteredIcons = icons.filter(icon =>
        icon.toString().includes(filter.toLowerCase()),
    );

    const handleIconSelected = icon => {
        if (icon === selectedIcon) {
            setSelectedIcon('');
        } else {
            setSelectedIcon(icon);
        }
    };

    // Handle background color update
    const handleUpdateBackgroundColor = newSelection => {
        setSelectedBackgroundColor(newSelection.left);
    };

    // Handle foreground color update
    const handleUpdateForegroundColor = newSelection => {
        setSelectedForegroundColor(newSelection.left);
    };

    const handlePortalChange = value => {
        const label = findPortalPageName(value);
        setSelectedPortal({ value: value, label: label });
    };

    const handleTypeChange = value => {
        setTypeSelection(value);
        if (value == 1) {
            setSelectedSSO(null);
            setSelectedPortal({});
            setSelectedMenuRoute('');
        } else if (value == 2) {
            setSelectedUrl('');
            setSelectedSSO(null);
            setSelectedMenuRoute('');
        } else if (value == 3) {
            setSelectedUrl('');
            setSelectedPortal({});
            setSelectedMenuRoute('');
        } else if (value == 4) {
            setSelectedUrl('');
            setSelectedSSO(null);
            setSelectedPortal({});
        }
    };

    const handleUrlChange = event => {
        const raw = event.target.value;
        isEditingRef.current = true;
        // Keep the full input as-is (user must include protocol)
        setSelectedUrl(raw);
        setSelectedSSO(null);
        setSelectedPortal({});
    };

    const handleUrlFocus = () => {
        isEditingRef.current = true;
    };

    const handleUrlBlur = () => {
        isEditingRef.current = false;
    };

    return (
        <>
            {!(isUploadFromDeviceChecked || isAddFromAssetsChecked) && (
                <>
                    <div>
                        <div
                            className="file-form-label"
                            style={{
                                width: '100%',
                                minWidth: '100%',
                                display:
                                    isUploadFromDeviceChecked ||
                                    isAddFromAssetsChecked
                                        ? 'none'
                                        : 'block',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        color: 'rgba(47, 43, 67, 1)',
                                        margin: '16px 0 4px',
                                    }}
                                >
                                    {getText('msg_3deb74565196')}
                                </div>
                                <span
                                    style={{
                                        color: '#EF4E45',
                                        marginLeft: '4px',
                                        fontWeight: '500',
                                    }}
                                >
                                    *
                                </span>
                            </div>
                            <Dropdown
                                name="typeDropdown"
                                defaultValue={getLabelForValue(typeSelection)}
                                options={typeOptions}
                                value={typeSelection}
                                onChange={value => handleTypeChange(value)}
                                disabled={false}
                            />
                        </div>
                    </div>

                    {typeSelection && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '8px',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    color: 'rgba(47, 43, 67, 1)',
                                    margin: '16px 0 4px',
                                }}
                            >
                                {getText('msg_d0517071aa37')}
                            </div>
                            <span
                                style={{
                                    color: '#EF4E45',
                                    marginLeft: '4px',
                                    fontWeight: '500',
                                }}
                            >
                                *
                            </span>
                        </div>
                    )}

                    {typeSelection === 1 && (
                        // Website: full width input, user types full URL including protocol
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <input
                                ref={urlInputRef}
                                onChange={e => handleUrlChange(e)}
                                onFocus={handleUrlFocus}
                                onBlur={handleUrlBlur}
                                style={{
                                    height: '48px',
                                    borderRadius: '12px',
                                    border: 'solid 1px rgba(47, 43, 67, 0.2)',
                                    paddingLeft: '16px',
                                    paddingRight: '16px',
                                    width: '100%',
                                }}
                                className="form-control"
                                type="text"
                                placeholder={getText('msg_b83cfb760fb4')}
                                value={selectedUrl}
                            />
                        </div>
                    )}

                    {typeSelection === 2 && (
                        <Dropdown
                            allowSearch={true}
                            defaultValue={selectedPortal.label || 'Select'}
                            options={eligiblePortalsList}
                            onChange={value => handlePortalChange(value)}
                        />
                    )}

                    {typeSelection === 3 && (
                        <SSODropdown
                            inlineLabel={false}
                            label=""
                            selectedSSO={item.linkPart?.tenantSSOId ?? 0}
                            onSSOChange={tenantSSOId =>
                                handleSSOChange(tenantSSOId)
                            }
                            client={true}
                            supportingText={getText('msg_4c7406e71780')}
                            displayRelative={false}
                        />
                    )}

                    {typeSelection === 4 && (
                        <Dropdown
                            name="menuRouteDropdown"
                            defaultValue={getLabelForMenuRoute(
                                selectedMenuRoute,
                            )}
                            options={menuRouteOptions}
                            value={selectedMenuRoute}
                            onChange={value => setSelectedMenuRoute(value)}
                            disabled={false}
                        />
                    )}
                </>
            )}

            {/* RADIO BUTTON OPTIONS FOR DISPLAY: ICON / MULTIMEDIA */}
            <div
                className="mt-4"
                style={{
                    width: '100%',
                    minWidth: '100%',
                    display:
                        isUploadFromDeviceChecked || isAddFromAssetsChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div className="file-form-label">
                    {getText('msg_574ff9b0c49c')}
                    <span
                        style={{
                            color: '#EF4E45',
                            marginLeft: '4px',
                            fontWeight: '500',
                        }}
                    >
                        *
                    </span>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="flexRadioIcon"
                        id="flexRadioIcon"
                        value="icon"
                        checked={isDisplayIcon === true}
                        onChange={() => setIsDisplayIcon(true)}
                        disabled={areInputsDisabled}
                    />
                    <label className="form-check-label" htmlFor="icon">
                        {getText('msg_716f63b96e0c')}
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="flexRadioUpload"
                        id="flexRadioUpload"
                        value="multimedia"
                        checked={isDisplayIcon === false}
                        onChange={() => setIsDisplayIcon(false)}
                        disabled={areInputsDisabled}
                    />
                    <label className="form-check-label" htmlFor="multimedia">
                        {getText('msg_50e19fda0d5b')}
                    </label>
                </div>
            </div>

            {isDisplayIcon ? (
                // IF isDisplayIcon IS TRUE, RENDER ICON PICKER, ICON PREVIEW, AND COLOR PICKERS
                <>
                    {/* ICON PICKER WITH SEARCH BAR */}
                    <div style={{ marginTop: '20px' }}>
                        <input
                            type="text"
                            className="form-control mb-3"
                            placeholder={getText('msg_2f149aaf731c')}
                            onChange={e => setFilter(e.target.value)}
                            disabled={areInputsDisabled}
                        />
                        <div
                            style={{
                                border: '1px solid #2F2B4333',
                                borderRadius: '12px',
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                className={`d-flex flex-wrap ${areInputsDisabled ? 'disabled' : ''}`}
                                style={{
                                    borderRadius: '12px',
                                    padding: '12px',
                                    height: '175px',
                                    overflow: areInputsDisabled
                                        ? 'hidden'
                                        : 'auto',
                                }}
                            >
                                {filteredIcons.map((icon, index) => (
                                    <div
                                        key={index}
                                        className={'px-2 d-flex'}
                                        style={{
                                            cursor: areInputsDisabled
                                                ? 'not-allowed'
                                                : 'pointer',
                                            backgroundColor:
                                                icon === selectedIcon
                                                    ? '#1366FF1A'
                                                    : 'transparent',
                                            outline:
                                                icon === selectedIcon
                                                    ? '1px solid #1366FF'
                                                    : 'none',
                                            borderRadius: '8px',
                                            aspectRatio: '1/1',
                                            height: 'fit-content',
                                            alignItems: 'center',
                                        }}
                                        onClick={() =>
                                            areInputsDisabled
                                                ? null
                                                : handleIconSelected(icon)
                                        }
                                    >
                                        <i
                                            className={'bi bi-' + icon}
                                            style={{ fontSize: '24px' }}
                                        ></i>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ICON PREVIEW */}
                    {selectedIcon ? (
                        <div
                            className="d-flex mt-4"
                            style={{
                                alignItems: 'center',
                                gap: '10px',
                                flexDirection: 'column',
                            }}
                        >
                            <label className="form-check-label">
                                {getText('msg_2ca4886b4abf')}
                            </label>
                            <i
                                className={'bi bi-' + selectedIcon}
                                style={{
                                    fontSize: '2em',
                                    color: 'selectedForegroundColor',
                                }}
                            ></i>
                        </div>
                    ) : null}

                    {/* BACKGROUND COLOR PICKER */}
                    <div className="py-3">
                        <ColorBar
                            label={getText('msg_64dd60fe1a04')}
                            initialColors={{
                                left: selectedBackgroundColor,
                                right: selectedBackgroundColor,
                                type: 'single',
                            }}
                            onChangeComplete={handleUpdateBackgroundColor}
                            allowGradient={false}
                            toolTipText=""
                            startBelowHeader={true}
                        />
                    </div>

                    {/* FOREGROUND COLOR PICKER */}
                    <div className="py-3">
                        <ColorBar
                            label={getText('msg_f7745efdedae')}
                            initialColors={{
                                left: selectedForegroundColor,
                                right: selectedForegroundColor,
                                type: 'single',
                            }}
                            onChangeComplete={handleUpdateForegroundColor}
                            allowGradient={false}
                            toolTipText=""
                            startBelowHeader={true}
                        />
                    </div>
                </>
            ) : (
                // IF isDisplayIcon IS FALSE, RENDER MULTIMEDIA COMPOSER
                <>
                    <div
                        className="mt-5"
                        style={{
                            display:
                                areInputsDisabled ||
                                isUploadFromDeviceChecked ||
                                isAddFromAssetsChecked
                                    ? 'none'
                                    : 'block',
                        }}
                    >
                        <div
                            className="d-flex justify-content-center mb-4"
                            style={{ flexDirection: 'column', gap: '24px' }}
                        >
                            <button
                                style={{ width: '100%' }}
                                className={`button-base ing-button-primary-fit-content`}
                                onClick={() => {
                                    setIsAddFromAssetsChecked('image');
                                    setIsUploadFromDeviceChecked('');
                                }}
                            >
                                {getText('msg_27eb0a10db5c')}
                            </button>
                            <button
                                style={{ width: '100%' }}
                                className={`button-base button-secondary`}
                                onClick={() => {
                                    setIsUploadFromDeviceChecked('image');
                                    setIsAddFromAssetsChecked('');
                                }}
                            >
                                {getText('msg_c002fe6df29c')}
                            </button>
                        </div>
                        <>
                            {image && (
                                <div
                                    className="d-flex"
                                    style={{
                                        height: '80%',
                                        width: '80%',
                                        flexDirection: 'column',
                                        margin: '0 auto',
                                    }}
                                >
                                    <img
                                        src={image}
                                        style={{
                                            height: '100%',
                                            width: '100%',
                                            borderRadius: '10px',
                                        }}
                                    />
                                </div>
                            )}
                            <div
                                className="fw-medium"
                                style={{
                                    fontSize: '16px',
                                    color: 'rgba(47, 43, 67, 1)',
                                    margin: '10px 0',
                                }}
                            >
                                {getText('msg_792aa0ea12f8')}
                            </div>
                            <textarea
                                className="form-control"
                                style={{
                                    height: '100px',
                                    borderRadius: '12px',
                                    border: 'solid 1px rgba(47, 43, 67, 0.2)',
                                    resize: 'none',
                                    padding: '16px',
                                }}
                                onChange={e => handleImageCaptionChange(e)}
                                placeholder={
                                    areInputsDisabled ? '' : '(Optional)'
                                }
                                defaultValue={selectedCaption || ''}
                                disabled={areInputsDisabled}
                            ></textarea>
                            <CharacterLimitIndicator
                                maxChars={50}
                                inputStr={selectedCaption || ''}
                            />
                        </>

                        {/* RICH TEXT EDITOR */}
                        <div className="file-form-label mt-5">
                            {getText('msg_c3328c39b0e2')}
                            <span
                                style={{
                                    color: '#EF4E45',
                                    marginLeft: '4px',
                                    fontWeight: '500',
                                }}
                            >
                                *
                            </span>
                        </div>
                        <div style={{ marginTop: '20px' }}>
                            {!isLoading && (
                                <RichTextEditor
                                    initialContent={initialText}
                                    onContentUpdate={setText}
                                    onUploadFromDevice={
                                        setIsUploadFromDeviceChecked
                                    }
                                    onAddFromAssets={setIsAddFromAssetsChecked}
                                    textAssets={textAssets}
                                    disableLink={true}
                                    portalId={portalId}
                                    onAssetDelete={handleAssetDelete}
                                    disableAssetLink={true}
                                    disableHomeLink={true}
                                />
                            )}
                        </div>

                        {/* BUTTON */}
                        <div className="file-form-label mt-5">
                            {getText('msg_794145f030ff')}
                            <span
                                style={{
                                    color: '#EF4E45',
                                    marginLeft: '4px',
                                    fontWeight: '500',
                                }}
                            >
                                *
                            </span>
                        </div>
                        <input
                            onChange={e => handleButtonLabelChange(e)}
                            style={{
                                height: '48px',
                                width: '319px',
                                borderRadius: '12px',
                                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            }}
                            className="form-control"
                            type="text"
                            placeholder={getText('msg_a29e5384f6af')}
                            defaultValue={selectedButtonText}
                            disabled={areInputsDisabled}
                        ></input>
                        <CharacterLimitIndicator
                            maxChars={10}
                            inputStr={selectedButtonText || ''}
                        />
                    </div>
                </>
            )}

            {/* Upload From Device */}
            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAsset(asset, 'name', value))
                        }
                        maxChars={256}
                        required={true}
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                ></i>
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(updateAsset(asset, 'assetTags', tags))
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAsset(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_77453f9dd036')}
                            checked={asset.assetTypeId !== 5} //UnassignedInProgress
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                            display: isUploadFromDeviceChecked
                                ? 'block'
                                : 'none',
                        }}
                    >
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={() => uploadAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {/* Add From Assets */}
            {isAddFromAssetsChecked && (
                <>
                    <div
                        className={styles.searchWrapper}
                        style={{ marginBottom: '26px' }}
                    >
                        <input
                            type="text"
                            className={styles.searchInput}
                            style={{ width: '100%' }}
                            placeholder={getText('msg_b18cdf8f03e9')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <span className={styles.searchIcon}>
                            <i className="bi bi-search"></i>
                        </span>
                    </div>

                    <AssetGridView
                        assets={assets}
                        propertiesModel={true}
                        handleClick={handleClick}
                        handleDoubleClick={() => null}
                        activeCard={activeCard}
                        fullAssetName={false}
                    />

                    {assets.length < 1 && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={
                                searchTerm
                                    ? 'Sorry, no results found'
                                    : 'Sorry, there are no assets to display'
                            }
                            description={
                                searchTerm
                                    ? 'Try adjusting your search to find what you are looking for'
                                    : `You can upload a New Asset by clicking "Cancel" on the bottom left corner, then clicking on "Upload from Device"`
                            }
                        />
                    )}

                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                            display: isAddFromAssetsChecked ? 'block' : 'none',
                        }}
                    >
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsAddFromAssetsChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={() => addAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default PropertiesLink;
